import React, { useState, useEffect } from 'react';

// ─── Category & Document Config ──────────────────────────────────────────────

const CATEGORIES = [
  {
    id: 'core',
    label: 'Core Financial',
    icon: '📊',
    docs: [
      {
        id: 'net-worth-certificate',
        label: 'Net Worth Certificate',
        desc: 'For visa, home loan, property deals, court cases',
        fields: [
          { key: 'name',          label: 'Applicant Full Name',       placeholder: 'As per PAN card' },
          { key: 'pan',           label: 'PAN Number',                placeholder: 'ABCDE1234F' },
          { key: 'address',       label: 'Address',                   placeholder: 'Complete residential address', type: 'textarea' },
          { key: 'assets',        label: 'Assets (one per line)',      placeholder: 'Residential Property – Rs.45,00,000\nFixed Deposits – Rs.12,00,000\nEquity / MF – Rs.8,00,000\nSavings Account Balance – Rs.2,00,000', type: 'textarea' },
          { key: 'liabilities',   label: 'Liabilities (one per line)', placeholder: 'Home Loan Outstanding – Rs.18,00,000\nPersonal Loan – Rs.2,00,000', type: 'textarea' },
          { key: 'purpose',       label: 'Purpose of Certificate',    placeholder: 'e.g. For submission to Embassy for Visa / Home Loan application' },
          { key: 'asOnDate',      label: 'As on Date',                placeholder: 'e.g. 31st March 2026' },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name of Chartered Accountant' },
          { key: 'membershipNo',  label: 'ICAI Membership Number',    placeholder: 'e.g. 123456' },
          { key: 'firmName',      label: 'CA Firm Name',              placeholder: 'Name of CA firm' },
          { key: 'place',         label: 'Place',                     placeholder: 'e.g. Hyderabad' },
        ],
      },
      {
        id: 'income-certificate',
        label: 'Income Certificate',
        desc: 'For loans, government schemes, college admissions',
        fields: [
          { key: 'name',          label: 'Name',                      placeholder: 'Full name' },
          { key: 'pan',           label: 'PAN Number',                placeholder: 'ABCDE1234F' },
          { key: 'address',       label: 'Address',                   placeholder: 'Complete address', type: 'textarea' },
          { key: 'assessmentYear',label: 'Assessment Year',           placeholder: 'e.g. 2025-26', type: 'select', options: ['2025-26','2024-25','2023-24','2022-23'] },
          { key: 'salaryIncome',  label: 'Income from Salary (Rs.)',  placeholder: '0 if not applicable' },
          { key: 'businessIncome',label: 'Income from Business/Profession (Rs.)', placeholder: '0 if not applicable' },
          { key: 'otherIncome',   label: 'Income from Other Sources (Rs.)', placeholder: '0 if not applicable' },
          { key: 'purpose',       label: 'Purpose',                   placeholder: 'e.g. For Bank Loan / Scholarship application' },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name' },
          { key: 'membershipNo',  label: 'ICAI Membership Number',    placeholder: 'e.g. 123456' },
          { key: 'firmName',      label: 'CA Firm Name',              placeholder: 'Name of CA firm' },
          { key: 'place',         label: 'Place',                     placeholder: 'e.g. Hyderabad' },
          { key: 'date',          label: 'Date',                      placeholder: 'e.g. 23rd April 2026' },
        ],
      },
      {
        id: 'balance-sheet',
        label: 'Balance Sheet & P&L',
        desc: 'For bank loans, annual filings, proprietorship/partnership',
        fields: [
          { key: 'entityName',    label: 'Entity / Business Name',    placeholder: 'Full legal name' },
          { key: 'entityType',    label: 'Type of Entity',            placeholder: 'Proprietorship', type: 'select', options: ['Proprietorship','Partnership Firm','LLP','Private Limited Company','Public Limited Company'] },
          { key: 'financialYear', label: 'Financial Year',            placeholder: 'e.g. 2025-26', type: 'select', options: ['2025-26','2024-25','2023-24'] },
          { key: 'fixedAssets',   label: 'Fixed Assets (Rs.)',        placeholder: 'e.g. 15,00,000' },
          { key: 'currentAssets', label: 'Current Assets (Rs.)',      placeholder: 'e.g. 8,00,000' },
          { key: 'capital',       label: 'Capital / Equity (Rs.)',    placeholder: 'e.g. 12,00,000' },
          { key: 'loans',         label: 'Loans & Borrowings (Rs.)',  placeholder: 'e.g. 7,00,000' },
          { key: 'currentLiabilities', label: 'Current Liabilities (Rs.)', placeholder: 'e.g. 4,00,000' },
          { key: 'revenue',       label: 'Total Revenue / Turnover (Rs.)', placeholder: 'e.g. 45,00,000' },
          { key: 'expenses',      label: 'Total Expenses (Rs.)',      placeholder: 'e.g. 38,00,000' },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name' },
          { key: 'membershipNo',  label: 'ICAI Membership Number',    placeholder: 'e.g. 123456' },
          { key: 'place',         label: 'Place',                     placeholder: 'e.g. Hyderabad' },
          { key: 'date',          label: 'Date',                      placeholder: 'e.g. 23rd April 2026' },
        ],
      },
      {
        id: 'projected-financials',
        label: 'Projected Financial Statements',
        desc: 'For startups, business loans, investor presentations',
        fields: [
          { key: 'entityName',    label: 'Entity / Business Name',    placeholder: 'Full legal name' },
          { key: 'nature',        label: 'Nature of Business',        placeholder: 'e.g. Software Services / Retail / Manufacturing' },
          { key: 'period',        label: 'Projection Period',         placeholder: '3 Years', type: 'select', options: ['1 Year','2 Years','3 Years','5 Years'] },
          { key: 'currentTurnover', label: 'Current Annual Turnover (Rs.)', placeholder: 'e.g. 20,00,000 (0 if startup)' },
          { key: 'year1Revenue',  label: 'Projected Revenue Year 1 (Rs.)', placeholder: 'e.g. 30,00,000' },
          { key: 'year2Revenue',  label: 'Projected Revenue Year 2 (Rs.)', placeholder: 'e.g. 50,00,000' },
          { key: 'year3Revenue',  label: 'Projected Revenue Year 3 (Rs.)', placeholder: 'e.g. 80,00,000' },
          { key: 'majorExpenses', label: 'Major Expense Heads',       placeholder: 'Staff costs – 40%\nRent – 10%\nMarketing – 15%', type: 'textarea' },
          { key: 'purpose',       label: 'Purpose',                   placeholder: 'e.g. For Bank Loan of Rs.50L / Investor pitch' },
          { key: 'assumptions',   label: 'Key Assumptions',           placeholder: 'e.g. 25% YoY growth, stable margins', type: 'textarea' },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name' },
          { key: 'membershipNo',  label: 'ICAI Membership Number',    placeholder: 'e.g. 123456' },
          { key: 'place',         label: 'Place',                     placeholder: 'e.g. Hyderabad' },
          { key: 'date',          label: 'Date',                      placeholder: 'e.g. 23rd April 2026' },
        ],
      },
    ],
  },
  {
    id: 'tax',
    label: 'Tax Documents',
    icon: '🧾',
    docs: [
      {
        id: 'itr-computation',
        label: 'ITR Computation Sheet',
        desc: 'Detailed income tax computation before filing',
        fields: [
          { key: 'name',          label: 'Taxpayer Name',             placeholder: 'Full name as per PAN' },
          { key: 'pan',           label: 'PAN Number',                placeholder: 'ABCDE1234F' },
          { key: 'assessmentYear',label: 'Assessment Year',           placeholder: 'e.g. 2025-26', type: 'select', options: ['2025-26','2024-25','2023-24'] },
          { key: 'regime',        label: 'Tax Regime',                placeholder: 'New Regime', type: 'select', options: ['New Regime (Default)','Old Regime'] },
          { key: 'grossSalary',   label: 'Gross Salary (Rs.)',        placeholder: 'Annual CTC / gross salary' },
          { key: 'hra',           label: 'HRA Exemption (Rs.)',       placeholder: '0 if not claiming' },
          { key: 'lta',           label: 'LTA Exemption (Rs.)',       placeholder: '0 if not claiming' },
          { key: 'standardDeduction', label: 'Standard Deduction (Rs.)', placeholder: '75000 (new regime) / 50000 (old)' },
          { key: 'sec80c',        label: '80C Investments (Rs.)',     placeholder: 'Max 1,50,000' },
          { key: 'sec80d',        label: '80D Health Insurance (Rs.)',placeholder: 'Self + family + parents' },
          { key: 'nps80ccd',      label: 'NPS 80CCD(1B) (Rs.)',       placeholder: 'Max 50,000' },
          { key: 'otherIncome',   label: 'Other Income (Rs.)',        placeholder: 'Interest, rent, capital gains, etc.' },
          { key: 'tdsDeducted',   label: 'TDS Already Deducted (Rs.)',placeholder: 'From Form 16 / 26AS' },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name' },
          { key: 'membershipNo',  label: 'ICAI Membership Number',    placeholder: 'e.g. 123456' },
        ],
      },
      {
        id: 'form-15cb',
        label: 'Form 15CB Certificate',
        desc: 'For foreign remittances — consultancy, royalties, services',
        fields: [
          { key: 'remitterName',  label: 'Remitter Name',             placeholder: 'Name of person / company sending money' },
          { key: 'remitterPAN',   label: 'Remitter PAN',              placeholder: 'ABCDE1234F' },
          { key: 'remitterAddress', label: 'Remitter Address',        placeholder: 'Complete address', type: 'textarea' },
          { key: 'beneficiaryName', label: 'Beneficiary Name',        placeholder: 'Name of foreign recipient' },
          { key: 'beneficiaryCountry', label: 'Beneficiary Country',  placeholder: 'e.g. United States' },
          { key: 'currency',      label: 'Currency',                  placeholder: 'e.g. USD / GBP / EUR' },
          { key: 'amount',        label: 'Amount (in foreign currency)', placeholder: 'e.g. 5000 USD' },
          { key: 'nature',        label: 'Nature of Remittance',      placeholder: 'e.g. Consultancy Fees / Royalty / Software Services' },
          { key: 'dtaa',          label: 'DTAA Applicable?',          placeholder: 'Yes / No / Not Applicable', type: 'select', options: ['Yes — DTAA benefit applicable','No — Full withholding tax applies','Not applicable'] },
          { key: 'taxRate',       label: 'TDS Rate Applied (%)',      placeholder: 'e.g. 10% under DTAA / 20% without DTAA' },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name' },
          { key: 'membershipNo',  label: 'ICAI Membership Number',    placeholder: 'e.g. 123456' },
          { key: 'firmName',      label: 'CA Firm Name',              placeholder: 'Name of CA firm' },
          { key: 'date',          label: 'Date',                      placeholder: 'e.g. 23rd April 2026' },
        ],
      },
      {
        id: 'tax-planning-letter',
        label: 'Tax Planning Letter',
        desc: 'Annual advisory with personalised deduction recommendations',
        fields: [
          { key: 'clientName',    label: 'Client Name',               placeholder: 'Full name' },
          { key: 'annualIncome',  label: 'Annual Income (Rs.)',        placeholder: 'Gross income before deductions' },
          { key: 'regime',        label: 'Tax Regime',                placeholder: 'New Regime', type: 'select', options: ['New Regime','Old Regime','Comparing both'] },
          { key: 'currentInvestments', label: 'Existing Investments', placeholder: 'e.g. PPF – Rs.50,000, ELSS – Rs.30,000, LIC – Rs.20,000', type: 'textarea' },
          { key: 'goals',         label: 'Financial Goals',           placeholder: 'e.g. Child education, retirement, home purchase', type: 'textarea' },
          { key: 'assessmentYear',label: 'Assessment Year',           placeholder: 'e.g. 2025-26' },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name' },
          { key: 'firmName',      label: 'CA Firm Name',              placeholder: 'Name of CA firm' },
          { key: 'date',          label: 'Date',                      placeholder: 'e.g. 23rd April 2026' },
        ],
      },
      {
        id: 'tds-reconciliation',
        label: 'TDS Reconciliation Statement',
        desc: 'Matches TDS deducted vs 26AS for the year',
        fields: [
          { key: 'name',          label: 'Name',                      placeholder: 'Full name' },
          { key: 'pan',           label: 'PAN Number',                placeholder: 'ABCDE1234F' },
          { key: 'assessmentYear',label: 'Assessment Year',           placeholder: 'e.g. 2025-26', type: 'select', options: ['2025-26','2024-25','2023-24'] },
          { key: 'tdsForm16',     label: 'TDS as per Form 16 / 16A (Rs.)', placeholder: 'Total TDS shown in salary/other certificates' },
          { key: 'tds26as',       label: 'TDS as per Form 26AS (Rs.)', placeholder: 'Total TDS reflected in 26AS / AIS' },
          { key: 'advance',       label: 'Advance Tax Paid (Rs.)',     placeholder: '0 if not applicable' },
          { key: 'selfAssessment',label: 'Self Assessment Tax Paid (Rs.)', placeholder: '0 if not applicable' },
          { key: 'differences',   label: 'Differences / Remarks',     placeholder: 'Explain any mismatch between Form 16 and 26AS', type: 'textarea' },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name' },
          { key: 'membershipNo',  label: 'ICAI Membership Number',    placeholder: 'e.g. 123456' },
          { key: 'date',          label: 'Date',                      placeholder: 'e.g. 23rd April 2026' },
        ],
      },
    ],
  },
  {
    id: 'audit',
    label: 'Audit & Compliance',
    icon: '🔍',
    docs: [
      {
        id: 'tax-audit-3cd',
        label: 'Tax Audit Report (3CD)',
        desc: 'For businesses with turnover > Rs.1Cr — Sec 44AB',
        fields: [
          { key: 'entityName',    label: 'Entity Name',               placeholder: 'Full legal name' },
          { key: 'pan',           label: 'PAN',                       placeholder: 'ABCDE1234F' },
          { key: 'assessmentYear',label: 'Assessment Year',           placeholder: 'e.g. 2025-26', type: 'select', options: ['2025-26','2024-25','2023-24'] },
          { key: 'turnover',      label: 'Total Turnover (Rs.)',      placeholder: 'e.g. 1,50,00,000' },
          { key: 'accountingMethod', label: 'Method of Accounting',   placeholder: 'Mercantile', type: 'select', options: ['Mercantile (Accrual)','Cash Basis'] },
          { key: 'changeInMethod',label: 'Change in Accounting Method?', placeholder: 'No', type: 'select', options: ['No','Yes — details in observations'] },
          { key: 'observations',  label: 'Key Audit Observations',    placeholder: 'Any qualifications, disallowances, or remarks', type: 'textarea' },
          { key: 'loansAbove20k', label: 'Loans/Deposits accepted in cash > Rs.20,000?', placeholder: 'No', type: 'select', options: ['No','Yes'] },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name' },
          { key: 'membershipNo',  label: 'ICAI Membership Number',    placeholder: 'e.g. 123456' },
          { key: 'firmName',      label: 'CA Firm Name',              placeholder: 'Firm name & registration number' },
          { key: 'place',         label: 'Place',                     placeholder: 'e.g. Hyderabad' },
          { key: 'date',          label: 'Date of Report',            placeholder: 'e.g. 23rd April 2026' },
        ],
      },
      {
        id: 'auditors-report',
        label: "Auditor's Report",
        desc: 'Statutory audit opinion under Companies Act 2013',
        fields: [
          { key: 'companyName',   label: 'Company Name',              placeholder: 'Full registered name' },
          { key: 'cin',           label: 'CIN',                       placeholder: 'Corporate Identification Number' },
          { key: 'financialYear', label: 'Financial Year',            placeholder: 'e.g. 2025-26' },
          { key: 'opinionType',   label: 'Type of Opinion',           placeholder: 'Unqualified', type: 'select', options: ['Unqualified (Clean)','Qualified','Adverse','Disclaimer of Opinion'] },
          { key: 'qualification', label: 'Qualification / Emphasis (if any)', placeholder: 'Leave blank for unqualified opinion', type: 'textarea' },
          { key: 'caroApplicable',label: 'CARO 2020 Applicable?',     placeholder: 'Yes', type: 'select', options: ['Yes','No'] },
          { key: 'keyAuditMatters', label: 'Key Audit Matters (if any)', placeholder: 'Revenue recognition, related party transactions, etc.', type: 'textarea' },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name' },
          { key: 'membershipNo',  label: 'ICAI Membership Number',    placeholder: 'e.g. 123456' },
          { key: 'firmName',      label: 'CA Firm Name & Reg. No.',   placeholder: 'Firm name and ICAI registration number' },
          { key: 'place',         label: 'Place',                     placeholder: 'e.g. Hyderabad' },
          { key: 'date',          label: 'Date',                      placeholder: 'e.g. 23rd April 2026' },
        ],
      },
      {
        id: 'gst-reconciliation',
        label: 'GST Reconciliation Certificate',
        desc: 'Books vs GSTR-1/3B vs GSTR-2A/2B reconciliation',
        fields: [
          { key: 'entityName',    label: 'Entity Name',               placeholder: 'Full legal name' },
          { key: 'gstin',         label: 'GSTIN',                     placeholder: '29ABCDE1234F1Z5' },
          { key: 'financialYear', label: 'Financial Year',            placeholder: 'e.g. 2024-25', type: 'select', options: ['2024-25','2023-24','2022-23'] },
          { key: 'turnoverBooks', label: 'Turnover as per Books (Rs.)', placeholder: 'e.g. 1,20,00,000' },
          { key: 'turnoverGSTR1', label: 'Turnover as per GSTR-1 (Rs.)', placeholder: 'e.g. 1,18,00,000' },
          { key: 'turnoverGSTR3B',label: 'Turnover as per GSTR-3B (Rs.)', placeholder: 'e.g. 1,18,00,000' },
          { key: 'itcBooks',      label: 'ITC as per Books (Rs.)',    placeholder: 'Input Tax Credit in books' },
          { key: 'itcGSTR2B',     label: 'ITC as per GSTR-2B (Rs.)', placeholder: 'ITC reflecting in GSTR-2B' },
          { key: 'differences',   label: 'Differences & Reasons',     placeholder: 'Explain any discrepancies', type: 'textarea' },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name' },
          { key: 'membershipNo',  label: 'ICAI Membership Number',    placeholder: 'e.g. 123456' },
          { key: 'place',         label: 'Place',                     placeholder: 'e.g. Hyderabad' },
          { key: 'date',          label: 'Date',                      placeholder: 'e.g. 23rd April 2026' },
        ],
      },
      {
        id: 'stock-audit',
        label: 'Stock Audit Report',
        desc: 'Required by banks for working capital loans against inventory',
        fields: [
          { key: 'companyName',   label: 'Company / Borrower Name',   placeholder: 'Full name' },
          { key: 'bank',          label: 'Bank Name & Branch',        placeholder: 'e.g. State Bank of India, Hitech City Branch' },
          { key: 'loanAccount',   label: 'Loan Account Number',       placeholder: 'Working capital / CC account number' },
          { key: 'auditDate',     label: 'Date of Stock Verification', placeholder: 'e.g. 20th April 2026' },
          { key: 'stockValue',    label: 'Stock Value as per Books (Rs.)', placeholder: 'e.g. 45,00,000' },
          { key: 'physicalStock', label: 'Physical Stock Verified (Rs.)', placeholder: 'e.g. 43,50,000' },
          { key: 'debtors',       label: 'Book Debts / Receivables (Rs.)', placeholder: 'e.g. 18,00,000' },
          { key: 'discrepancies', label: 'Discrepancies / Observations', placeholder: 'Mention any differences, slow-moving stock, obsolete items', type: 'textarea' },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name' },
          { key: 'membershipNo',  label: 'ICAI Membership Number',    placeholder: 'e.g. 123456' },
          { key: 'firmName',      label: 'CA Firm Name',              placeholder: 'Firm name' },
          { key: 'place',         label: 'Place',                     placeholder: 'e.g. Hyderabad' },
          { key: 'date',          label: 'Date of Report',            placeholder: 'e.g. 23rd April 2026' },
        ],
      },
    ],
  },
  {
    id: 'certificates',
    label: 'Certificates',
    icon: '📄',
    docs: [
      {
        id: 'turnover-certificate',
        label: 'Turnover Certificate',
        desc: 'For government tenders, MSME registration, bank limits',
        fields: [
          { key: 'entityName',    label: 'Entity / Business Name',    placeholder: 'Full legal name' },
          { key: 'pan',           label: 'PAN',                       placeholder: 'ABCDE1234F' },
          { key: 'gstin',         label: 'GSTIN (if applicable)',     placeholder: 'Leave blank if not registered' },
          { key: 'fy1',           label: 'Financial Year 1',          placeholder: 'e.g. 2022-23', type: 'select', options: ['2024-25','2023-24','2022-23','2021-22'] },
          { key: 'turnover1',     label: 'Turnover FY1 (Rs.)',        placeholder: 'e.g. 80,00,000' },
          { key: 'fy2',           label: 'Financial Year 2',          placeholder: 'e.g. 2023-24', type: 'select', options: ['2024-25','2023-24','2022-23','2021-22'] },
          { key: 'turnover2',     label: 'Turnover FY2 (Rs.)',        placeholder: 'e.g. 1,10,00,000' },
          { key: 'fy3',           label: 'Financial Year 3',          placeholder: 'e.g. 2024-25', type: 'select', options: ['2024-25','2023-24','2022-23','2021-22'] },
          { key: 'turnover3',     label: 'Turnover FY3 (Rs.)',        placeholder: 'e.g. 1,45,00,000' },
          { key: 'purpose',       label: 'Purpose',                   placeholder: 'e.g. For submission to [Authority] for tender / loan' },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name' },
          { key: 'membershipNo',  label: 'ICAI Membership Number',    placeholder: 'e.g. 123456' },
          { key: 'firmName',      label: 'CA Firm Name',              placeholder: 'Firm name' },
          { key: 'place',         label: 'Place',                     placeholder: 'e.g. Hyderabad' },
          { key: 'date',          label: 'Date',                      placeholder: 'e.g. 23rd April 2026' },
        ],
      },
      {
        id: 'utilisation-certificate',
        label: 'Utilisation Certificate',
        desc: 'Certifies grant/loan funds used for stated purpose',
        fields: [
          { key: 'orgName',       label: 'Organisation Name',         placeholder: 'Full name of organization' },
          { key: 'pan',           label: 'PAN',                       placeholder: 'ABCDE1234F' },
          { key: 'schemeName',    label: 'Scheme / Grant Name',       placeholder: 'e.g. MSME Development Fund / Government Grant' },
          { key: 'authority',     label: 'Sanctioning Authority',     placeholder: 'e.g. District Industries Centre / Ministry of MSME' },
          { key: 'sanctionedAmount', label: 'Amount Sanctioned (Rs.)', placeholder: 'e.g. 10,00,000' },
          { key: 'receivedAmount',label: 'Amount Received (Rs.)',     placeholder: 'e.g. 10,00,000' },
          { key: 'utilisedAmount',label: 'Amount Utilised (Rs.)',     placeholder: 'e.g. 9,85,000' },
          { key: 'purpose',       label: 'Purpose of Utilisation',    placeholder: 'e.g. Purchase of machinery, working capital', type: 'textarea' },
          { key: 'period',        label: 'Period of Utilisation',     placeholder: 'e.g. April 2025 to March 2026' },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name' },
          { key: 'membershipNo',  label: 'ICAI Membership Number',    placeholder: 'e.g. 123456' },
          { key: 'firmName',      label: 'CA Firm Name',              placeholder: 'Firm name' },
          { key: 'place',         label: 'Place',                     placeholder: 'e.g. Hyderabad' },
          { key: 'date',          label: 'Date',                      placeholder: 'e.g. 23rd April 2026' },
        ],
      },
      {
        id: 'engagement-letter',
        label: 'CA Engagement Letter',
        desc: 'Agreement between CA and client — scope, fees, responsibilities',
        fields: [
          { key: 'clientName',    label: 'Client Name',               placeholder: 'Individual or entity name' },
          { key: 'clientAddress', label: 'Client Address',            placeholder: 'Complete address', type: 'textarea' },
          { key: 'caName',        label: 'CA Name',                   placeholder: 'Full name' },
          { key: 'firmName',      label: 'CA Firm Name',              placeholder: 'Firm name' },
          { key: 'membershipNo',  label: 'ICAI Membership Number',    placeholder: 'e.g. 123456' },
          { key: 'scope',         label: 'Scope of Work',             placeholder: 'e.g. Preparation of financial statements, ITR filing, GST returns for FY 2025-26', type: 'textarea' },
          { key: 'fees',          label: 'Professional Fees (Rs.)',   placeholder: 'e.g. Rs.25,000 per annum' },
          { key: 'paymentTerms',  label: 'Payment Terms',             placeholder: 'e.g. 50% on engagement, 50% on completion' },
          { key: 'period',        label: 'Engagement Period',         placeholder: 'e.g. FY 2025-26 (April 2025 to March 2026)' },
          { key: 'clientResponsibilities', label: 'Client Responsibilities', placeholder: 'e.g. Provide books of accounts, bank statements, invoices by 15th of each month', type: 'textarea' },
          { key: 'date',          label: 'Date',                      placeholder: 'e.g. 23rd April 2026' },
          { key: 'place',         label: 'Place',                     placeholder: 'e.g. Hyderabad' },
        ],
      },
    ],
  },
];

// ─── Print / Download Helper ─────────────────────────────────────────────────

function downloadCADoc(text, title) {
  const escaped = text.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const w = window.open('', '_blank', 'width=860,height=720');
  w.document.write(`<!DOCTYPE html><html><head><title>${title} — Axigon</title><style>
    body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;max-width:720px;margin:40px auto;color:#111;line-height:1.8;font-size:14px}
    h1{font-size:22px;margin:0 0 4px;text-align:center;text-transform:uppercase;letter-spacing:.04em}
    .subtitle{text-align:center;color:#666;font-size:12px;margin:0 0 32px;border-bottom:2px solid #111;padding-bottom:16px}
    .content{white-space:pre-wrap;line-height:1.95;font-family:Georgia,'Times New Roman',serif}
    .footer{margin-top:40px;font-size:11px;color:#999;border-top:1px solid #eee;padding-top:14px;text-align:center}
    .dl-btn{position:fixed;top:16px;right:16px;padding:9px 20px;background:#1a1a2e;color:#fff;border:none;border-radius:7px;cursor:pointer;font-size:13px;font-family:inherit}
    @media print{.dl-btn{display:none}}
  </style></head><body>
  <h1>${title}</h1>
  <div class="subtitle">Prepared by a Chartered Accountant | ICAI Member</div>
  <div class="content">${escaped}</div>
  <div class="footer">Generated via Axigon AI · For reference only · CA to review and sign before submission</div>
  <button class="dl-btn" onclick="window.print()">Save as PDF</button>
  </body></html>`);
  w.document.close();
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CADocuments({ goBack, embedded = false }) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [activeCategory, setActiveCategory] = useState('core');

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  const [activeDoc, setActiveDoc] = useState(null);
  const [step, setStep] = useState('list');
  const [fields, setFields] = useState({});
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const category = CATEGORIES.find(c => c.id === activeCategory);

  const setField = (k) => (e) => setFields(f => ({ ...f, [k]: e.target.value }));

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setStep('generating');
    try {
      const res = await fetch('/api/finance?action=generate-ca-doc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ docId: activeDoc.id, fields }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      setResult(data.document);
      setStep('result');
    } catch (err) {
      setError(err.message);
      setStep('form');
    }
    setLoading(false);
  };

  const selectDoc = (doc) => { setActiveDoc(doc); setFields({}); setResult(''); setError(''); setStep('form'); };
  const backToList = () => { setActiveDoc(null); setStep('list'); setError(''); };

  const iStyle = { width: '100%', padding: '10px 13px', borderRadius: '8px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  const lStyle = { fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: '5px' };
  const darkCard = { backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '12px' };

  const showMobilePills = isMobile || embedded;

  return (
    <section style={{ backgroundColor: 'var(--bg)', display: 'flex', flexDirection: showMobilePills ? 'column' : 'row', ...(embedded ? { height: '100%' } : { minHeight: '100vh', paddingTop: '64px' }) }}>

      {showMobilePills ? (
        /* ── Mobile / Embedded: horizontal pill nav ── */
        <div style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)', padding: '12px 16px', flexShrink: 0 }}>
          {!embedded && (
            <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '13px', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>← Back</button>
              <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>CA Documents</span>
              <span style={{ width: '48px' }} />
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '2px' }}>
            {CATEGORIES.map(cat => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => { setActiveCategory(cat.id); setActiveDoc(null); setStep('list'); }}
                  style={{
                    flexShrink: 0, padding: '7px 14px', borderRadius: '20px', border: 'none',
                    cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', fontWeight: isActive ? 700 : 500,
                    backgroundColor: isActive ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.04)',
                    color: isActive ? '#A5B4FC' : 'var(--text-secondary)',
                    outline: isActive ? '1px solid rgba(99,102,241,0.4)' : '1px solid transparent',
                    transition: 'all 0.15s',
                  }}
                >{cat.icon} {cat.label}</button>
              );
            })}
          </div>
        </div>
      ) : (
        /* ── Desktop: vertical sidebar ── */
        <div style={{ width: '200px', flexShrink: 0, backgroundColor: 'var(--bg-secondary)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', position: 'sticky', top: '64px' }}>
          <div style={{ padding: '16px 14px 12px', borderBottom: '1px solid var(--border)' }}>
            <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '13px', cursor: 'pointer', padding: 0, marginBottom: '12px', fontFamily: 'inherit' }}>← Back</button>
            <p style={{ fontSize: '14px', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>CA Documents</p>
            <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: '3px 0 0' }}>ICAI standard formats</p>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px 8px' }}>
            {CATEGORIES.map(cat => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => { setActiveCategory(cat.id); setActiveDoc(null); setStep('list'); }}
                  style={{
                    width: '100%', textAlign: 'left', padding: '12px 14px', borderRadius: '9px',
                    border: 'none', marginBottom: '3px', cursor: 'pointer', fontFamily: 'inherit',
                    backgroundColor: isActive ? 'rgba(99,102,241,0.15)' : 'transparent',
                    outline: isActive ? '1px solid rgba(99,102,241,0.35)' : 'none',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <span style={{ fontSize: '16px', display: 'block', marginBottom: '4px' }}>{cat.icon}</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: isActive ? '#A5B4FC' : 'var(--text-secondary)', lineHeight: 1.3 }}>{cat.label}</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'block', marginTop: '2px' }}>{cat.docs.length} documents</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Main content area ── */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ maxWidth: '820px', padding: '32px 36px' }}>

          {/* ── Document list ── */}
          {step === 'list' && (
            <>
              <div style={{ marginBottom: '28px' }}>
                <div style={{ display: 'inline-block', backgroundColor: 'rgba(0,194,124,0.08)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>
                  {category.icon} {category.label}
                </div>
                <h1 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.02em' }}>{category.label} Documents</h1>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
                {category.docs.map(doc => (
                  <button
                    key={doc.id}
                    onClick={() => selectDoc(doc)}
                    style={{ ...darkCard, padding: '22px', textAlign: 'left', cursor: 'pointer', background: 'var(--bg-secondary)', border: '1px solid var(--border)', transition: 'all 0.2s', borderTop: '2px solid var(--accent)' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.4)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.borderTopColor = 'var(--accent)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <p style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px' }}>{doc.label}</p>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 16px', lineHeight: 1.5 }}>{doc.desc}</p>
                    <span style={{ fontSize: '12px', color: 'var(--accent)', fontWeight: 600 }}>Prepare document →</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* ── Form ── */}
          {(step === 'form' || step === 'generating') && activeDoc && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <button onClick={backToList} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>← {category.label}</button>
                <span style={{ color: 'var(--border)' }}>›</span>
                <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>{activeDoc.label}</span>
              </div>

              {step === 'generating' ? (
                <div style={{ textAlign: 'center', marginTop: '80px' }}>
                  <div style={{ width: '44px', height: '44px', border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.9s linear infinite', margin: '0 auto 16px' }} />
                  <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Preparing {activeDoc.label}…</p>
                </div>
              ) : (
                <form onSubmit={handleGenerate} style={{ maxWidth: '580px' }}>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px', lineHeight: 1.6 }}>
                    Fill in the required details. The document will be generated in standard ICAI format ready for CA review and download.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {activeDoc.fields.map(field => (
                      <div key={field.key}>
                        <label style={lStyle}>{field.label}</label>
                        {field.type === 'textarea' ? (
                          <textarea style={{ ...iStyle, height: '88px', resize: 'vertical' }} placeholder={field.placeholder} value={fields[field.key] || ''} onChange={setField(field.key)} />
                        ) : field.type === 'select' ? (
                          <select style={{ ...iStyle, cursor: 'pointer' }} value={fields[field.key] || ''} onChange={setField(field.key)}>
                            <option value="">Select…</option>
                            {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input style={iStyle} placeholder={field.placeholder} value={fields[field.key] || ''} onChange={setField(field.key)} />
                        )}
                      </div>
                    ))}
                  </div>
                  {error && <p style={{ fontSize: '13px', color: '#EF4444', margin: '12px 0 0' }}>{error}</p>}
                  <button type="submit" style={{ marginTop: '22px', width: '100%', padding: '13px', borderRadius: '9px', background: 'linear-gradient(135deg, var(--accent), var(--accent))', color: 'white', border: 'none', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Prepare {activeDoc.label} →
                  </button>
                </form>
              )}
            </>
          )}

          {/* ── Result ── */}
          {step === 'result' && activeDoc && result && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <button onClick={backToList} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>← {category.label}</button>
                  <span style={{ color: 'var(--border)' }}>›</span>
                  <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>{activeDoc.label}</span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => downloadCADoc(result, activeDoc.label)}
                    style={{ background: 'linear-gradient(135deg,var(--accent),var(--accent))', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 18px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                  >Download PDF ↓</button>
                  <button
                    onClick={() => { navigator.clipboard.writeText(result).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); }}
                    style={{ background: 'transparent', border: `1px solid ${copied ? '#10B981' : 'var(--border)'}`, borderRadius: '8px', padding: '9px 18px', fontSize: '13px', fontWeight: 600, color: copied ? '#10B981' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}
                  >{copied ? '✓ Copied' : 'Copy text'}</button>
                  <button
                    onClick={() => { setStep('form'); setResult(''); }}
                    style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', padding: '9px 14px', fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit' }}
                  >Edit</button>
                </div>
              </div>
              <div style={{ ...darkCard, padding: '32px 36px', marginBottom: '12px' }}>
                <pre style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.9, margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'Georgia, "Times New Roman", serif' }}>{result}</pre>
              </div>
              <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
                AI-generated draft in ICAI standard format. Your CA must review, sign, and stamp before submission. This is not a certified document.
              </p>
            </>
          )}

        </div>
      </div>
    </section>
  );
}
